class PageTransition extends HTMLElement {

  constructor() {
    super();

    window.addEventListener("beforeunload", () => {
      document.body.classList.add("page-loading");

      document.dispatchEvent(new CustomEvent("pageTransitionStart", {
        bubbles: true,
        detail: {
          direction: "out",
          timestamp: Date.now()
        }
      }));
    });

    const initOnLoad = () => {
      document.dispatchEvent(new CustomEvent("pageLoaded", {
        bubbles: true,
        detail: { timestamp: Date.now() }
      }));

      this.hide();
    };

    if (document.readyState === "loading") {
      window.addEventListener("DOMContentLoaded", initOnLoad, { once: true });
    } else {
      initOnLoad();
    }

    window.addEventListener("pageshow", (event) => {
      if (event.persisted) {
        document.body.classList.remove("page-loading");
      }
    });
  }

  hide() {
    this.style.transition = "opacity 1s";
    this.style.opacity = 0;

    setTimeout(() => {
      this.hidden = true;

      document.dispatchEvent(new CustomEvent("pageTransitionEnd", {
        bubbles: true,
        detail: { timestamp: Date.now() }
      }));

    }, 1200);
  }
}

customElements.define("page-transition", PageTransition);

 
 class LogoList extends HTMLElement {
  constructor() {
    super();
    this.animations = [];
  }

  connectedCallback() {
    this.initMarquee();
  }

  initMarquee() {
    const marquees = this.querySelectorAll('.marquee');
    if (!marquees.length) return;

    marquees.forEach((marquee) => {
      const track = marquee.querySelector('.marquee_track');
      if (!track) return;

      const direction = marquee.dataset.direction || 'left';
      const speed = parseFloat(marquee.dataset.speed || 3);

      if (!track.dataset.cloned) {
        track.innerHTML += track.innerHTML;
        track.dataset.cloned = 'true';
      }

      let position = 0;
      const moveSpeed = speed * 0.3;

      const animate = () => {
        if (!this.isConnected) return;

        if (direction === 'left') {
          position -= moveSpeed;
          if (Math.abs(position) >= track.scrollWidth / 2) {
            position = 0;
          }
        } else {
          position += moveSpeed;
          if (position >= 0) {
            position = -track.scrollWidth / 2;
          }
        }

        track.style.transform = `translateX(${position}px)`;
        const raf = requestAnimationFrame(animate);
        this.animations.push(raf);
      };

      animate();
    });
  }

  disconnectedCallback() {
    this.animations.forEach((id) => cancelAnimationFrame(id));
    this.animations = [];
  }
}

if (!customElements.get('logo-list')) {
  customElements.define('logo-list', LogoList);
}


class FooterSection extends HTMLElement {
    constructor() {
        super(), this.maxShift = 40, this.animationZone = 50
    }
    connectedCallback() {
        window.addEventListener("scroll", this.onScroll.bind(this))
    }
    onScroll() {
        const scrollTop = window.scrollY,
            docHeight = document.documentElement.scrollHeight,
            winHeight = window.innerHeight,
            distanceFromBottom = docHeight - (scrollTop + winHeight),
            shift = Math.min(Math.max((this.animationZone - distanceFromBottom) / this.animationZone, 0), 1) * this.maxShift;
        this.style.setProperty("--footer-shift", `${shift}px`)
    }
}
customElements.define("footer-section", FooterSection);
class AccordionComponent extends HTMLElement {
  constructor() {
    super();
    this.items = [];
  }

  connectedCallback() {
    // Only direct children (important for nested)
    this.items = this.querySelectorAll(':scope > details');

    this.items.forEach((item) => {
      const summary = item.querySelector(':scope > summary');
      const content = item.querySelector(':scope > .accordion__content');

      if (!summary || !content) return;

      // initial state
      if (item.hasAttribute('open')) {
        content.style.height = 'auto';
      } else {
        content.style.height = '0px';
        content.style.overflow = 'hidden';
      }

      summary.addEventListener('click', (e) => {
        e.preventDefault();

        // critical for nested accordions
        e.stopPropagation();

        const isOpen = item.hasAttribute('open');
        const singleOpen = this.dataset.singleOpen !== "false";

        // close siblings (same level only)
        if (singleOpen) {
          this.items.forEach((i) => {
            if (i !== item) this.close(i);
          });
        }

        if (isOpen) {
          this.close(item);
        } else {
          this.open(item);
        }
      });
    });
  }

  open(item) {
    const content = item.querySelector(':scope > .accordion__content');
    if (!content) return;

    item.setAttribute('open', true);

    content.style.overflow = 'hidden';
    content.style.height = '0px';

    const height = content.scrollHeight;

    requestAnimationFrame(() => {
      content.style.transition = 'height 300ms ease';
      content.style.height = height + 'px';
    });

    content.addEventListener(
      'transitionend',
      () => {
        content.style.height = '';
        content.style.overflow = '';
      },
      { once: true }
    );
  }

  close(item) {
    const content = item.querySelector(':scope > .accordion__content');
    if (!content) return;

    const height = content.scrollHeight;

    content.style.overflow = 'hidden';
    content.style.height = height + 'px';

    requestAnimationFrame(() => {
      content.style.transition = 'height 300ms ease';
      content.style.height = '0px';
    });

    content.addEventListener(
      'transitionend',
      () => {
        item.removeAttribute('open');
        content.style.height = '';
        content.style.overflow = '';
      },
      { once: true }
    );
  }
}

if (!customElements.get('accordion-component')) {
  customElements.define('accordion-component', AccordionComponent);
}

class ShowMoreComponent extends HTMLElement {
  constructor() {
    super();
    this.expanded = false;
    this.animation = null;
    this.duration = 300;
  }

  connectedCallback() {
    this.content = this.querySelector('[data-show-more-content]');
    this.items = Array.from(this.querySelectorAll('[data-show-more-item]'));
    this.button = this.querySelector('[data-show-more-button]');

    if (!this.content || !this.items.length || !this.button) return;

    this.limit = parseInt(this.dataset.limit) || 6;

    this.setup();
    this.button.addEventListener('click', this.toggle.bind(this));
  }

  setup() {
    this.items.forEach((item, index) => {
      if (index >= this.limit) {
        item.classList.add('hidden');
      }
    });

    this.collapsedHeight = this.content.offsetHeight;
    this.updateState();
  }

  expand() {
    const startHeight = this.content.offsetHeight;

    this.items.forEach((item) => item.classList.remove('hidden'));

    const endHeight = this.content.scrollHeight;

    return { startHeight, endHeight };
  }

  collapse() {
    const startHeight = this.content.offsetHeight;

    this.items.forEach((item, index) => {
      if (index >= this.limit) {
        item.classList.add('hidden');
      }
    });

    const endHeight = this.collapsedHeight;

    return { startHeight, endHeight };
  }

  animate(start, end) {
    const content = this.content;

    content.style.overflow = 'hidden';

    if (this.animation) this.animation.cancel();

    this.animation = content.animate(
      {
        height: [start + 'px', end + 'px']
      },
      {
        duration: this.duration,
        easing: 'ease-in-out',
        fill: 'forwards'
      }
    );

    this.animation.onfinish = () => {
      content.style.height = '';
      content.style.overflow = '';
      this.expanded = !this.expanded;
      this.updateState();
    };
  }

  updateState() {
    this.dataset.expanded = this.expanded ? 'true' : 'false';
    this.button.setAttribute('aria-expanded', this.dataset.expanded);
  }

  toggle(e) {
    e.preventDefault();

    const { startHeight, endHeight } = this.expanded
      ? this.collapse()
      : this.expand();

    this.animate(startHeight, endHeight);
  }
}

if (!customElements.get('show-more-component')) {
  customElements.define('show-more-component', ShowMoreComponent);
}


class DialogComponent extends HTMLElement {
  constructor() {
    super();
    this.handleKeydown = this.handleKeydown.bind(this);
  }

  connectedCallback() {
    this.container = this.querySelector('[data-landing]');
    this.wrapper = this.querySelector('.dailogue_wrapper');

    if (!this.container || !this.wrapper) return;

    this.addEventListener('click', (e) => {

      // OPEN
      const trigger = e.target.closest('[data-target]');
      if (trigger && this.contains(trigger)) {
        e.preventDefault();
        this.open(trigger);
        return;
      }

      // CLOSE BUTTON
      if (e.target.closest('.dialog__close')) {
        this.close();
        return;
      }

      // CLICK OUTSIDE
      if (!e.target.closest('.dailogue_wrapper')) {
        this.close();
      }
    });
  }

  open(triggerBtn) {
    this.lastFocused = triggerBtn || document.activeElement;

    this.container.classList.add('active');

    this.focusableEls = this.getFocusable(this.wrapper);

    let target =
      this.wrapper.querySelector('input:not([type="hidden"])') ||
      this.wrapper.querySelector('.dialog__close') ||
      this.focusableEls[0];

    if (!target) {
      this.wrapper.setAttribute('tabindex', '-1');
      target = this.wrapper;
    }

    requestAnimationFrame(() => {
      target.focus();
    });

    document.addEventListener('keydown', this.handleKeydown);
  }

  close() {
    this.container.classList.remove('active');

    document.removeEventListener('keydown', this.handleKeydown);

    if (this.lastFocused) {
      this.lastFocused.focus();
      this.lastFocused = null;
    }
  }

  getFocusable(scope) {
    return Array.from(
      scope.querySelectorAll(`
        a[href],
        button:not([disabled]),
        input:not([type="hidden"]):not([disabled]),
        select:not([disabled]),
        textarea:not([disabled]),
        [tabindex]:not([tabindex="-1"])
      `)
    ).filter(el => el.offsetParent !== null);
  }

  handleKeydown(e) {
    if (!this.container.classList.contains('active')) return;

    // ESC CLOSE
    if (e.key === 'Escape') {
      this.close();
      return;
    }

    if (e.key !== 'Tab') return;

    this.focusableEls = this.getFocusable(this.wrapper);

    if (!this.focusableEls.length) return;

    const first = this.focusableEls[0];
    const last = this.focusableEls[this.focusableEls.length - 1];

    // SINGLE ELEMENT CASE
    if (this.focusableEls.length === 1) {
      e.preventDefault();
      first.focus();
      return;
    }

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }
}

if (!customElements.get('dailog-component')) {
  customElements.define('dailog-component', DialogComponent);
}



 class TimeLine extends HTMLElement {
  constructor() {
    super();
    this.handleResize = this.handleResize.bind(this);
  }

  connectedCallback() {
    this.wrapper = this.querySelector('.timeline_image_with_text_wrapper');
    this.content = this.querySelector('.timeline_content');
    this.media = this.querySelector('.timeline_media');

    if (!this.wrapper || !this.content || !this.media) return;

    this.update();

    // lightweight resize handling
    window.addEventListener('resize', this.handleResize);

    // better than resize (only if supported)
    if (window.ResizeObserver) {
      this.ro = new ResizeObserver(() => this.update());
      this.ro.observe(this.content);
      this.ro.observe(this.media);
    }
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.handleResize);
    if (this.ro) this.ro.disconnect();
  }

  handleResize() {
    this.update();
  }

  update() {
    const contentHeight = this.content.offsetHeight;
    const mediaHeight = this.media.offsetHeight;

    // reset classes
    this.wrapper.classList.remove('content_sticky', 'media_sticky');

    if (contentHeight > mediaHeight) {
      this.wrapper.classList.add('content_sticky');
    } else if (mediaHeight > contentHeight) {
      this.wrapper.classList.add('media_sticky');
    }
  }
}

if (!customElements.get('time-line')) {
  customElements.define('time-line', TimeLine);
}



class VideoSection extends HTMLElement {
  constructor() {
    super();
    this.playBtn = this.querySelector('.video_play_btn');
    this.imgWrapper = this.querySelector('.featured-video-poster_img_wrapper');
    this.vdoWrapper = this.querySelector('.featured-video-video_wrapper');
    this.video = this.querySelector('video');
    this.iframe = this.querySelector('iframe');
  }

 connectedCallback() {
    this.playBtn = this.querySelector('.video_play_btn');
    this.imgWrapper = this.querySelector('.featured-video-poster_img_wrapper');
    this.vdoWrapper = this.querySelector('.featured-video-video_wrapper');
    this.video = this.querySelector('video');
    this.iframe = this.querySelector('iframe');

    if (this.playBtn) {
      this.playBtn.addEventListener(
        'click',
        this.handleClick.bind(this)
      );
    }

    if (this.video) {
      this.video.setAttribute('tabindex', '-1');
      this.video.setAttribute('aria-hidden', 'true');
      this.video.addEventListener(
        'click',
        this.handleVdoClick.bind(this)
      );
    }

    if (this.iframe) {
      this.iframe.setAttribute('tabindex', '-1');
      this.iframe.setAttribute('aria-hidden', 'true');
    }
  }


 handleClick() {
  if (!this.imgWrapper || !this.vdoWrapper) return;

  this.imgWrapper.classList.add('hide');

  /* Remove play button from tab order */
  this.playBtn.setAttribute('tabindex', '-1');
  this.playBtn.setAttribute('aria-hidden', 'true');

  /* Enable focus on video */
  if (this.video) {
    this.video.removeAttribute('tabindex');
    this.video.removeAttribute('aria-hidden');
    this.video.focus();
    this.video.play();
  } else if (this.iframe) {
    this.iframe.removeAttribute('tabindex');
    this.iframe.removeAttribute('aria-hidden');

    const src = this.iframe.getAttribute("src");

    if (src.includes("youtube")) {
      this.iframe.contentWindow.postMessage(
        '{"event":"command","func":"playVideo","args":""}',
        "*"
      );
    } else if (src.includes("vimeo")) {
      this.iframe.contentWindow.postMessage(
        '{"method":"play"}',
        "https://player.vimeo.com"
      );
    }

    this.iframe.focus();
  }
}


  handleVdoClick() {
    if(this.vdoWrapper.classList.contains('controls_enabled')) {} else {
      if(this.video.paused) {
        this.video.play();
      } else {
        this.video.pause();
      }
    }
  }
}

 customElements.define('featured-video', VideoSection);

 
class HeroSlideshow extends HTMLElement {
  constructor() {
    super();

    this.swiper = null;

    this.sectionId = this.dataset.sectionId || "";
    this.spacing = Number(this.dataset.spacing) || 0;
    this.autoplay = this.dataset.autoplay === "true";
    this.speed = Number(this.dataset.autoplaySpeed) * 1000 || 3000;
    this.effect = this.dataset.effect || "slide";

    this.slider = this.querySelector(
      `.hero-slideshow__slider--${this.sectionId}`
    );

    this.nextBtn = this.querySelector(
      `.hero-slideshow__next--${this.sectionId}`
    );

    this.prevBtn = this.querySelector(
      `.hero-slideshow__prev--${this.sectionId}`
    );

    this.paginationEl = this.querySelector(
      `.swiper-pagination--${this.sectionId}`
    );
  }

  connectedCallback() {
    if (!this.slider) {
      console.warn(`Swiper container missing for section: ${this.sectionId}`);
      return;
    }

    initSwiper(() => {
      this.init();
    });
  }

  init() {
    // destroy existing instance (important for Shopify editor)
    if (this.slider.swiper) {
      this.slider.swiper.destroy(true, true);
    }

    this.swiper = new Swiper(this.slider, {
      slidesPerView: 1,
      spaceBetween: this.spacing,
      speed: 800,
      effect: this.effect,

      autoplay: this.autoplay
        ? {
            delay: this.speed,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }
        : false,

      navigation:
        this.nextBtn && this.prevBtn
          ? {
              nextEl: this.nextBtn,
              prevEl: this.prevBtn,
            }
          : false,

      pagination:
        this.paginationEl
          ? {
              el: this.paginationEl,
              clickable: true,
            }
          : false,

      observer: true,
      observeParents: true,

      a11y: {
        enabled: true,
      },
      on: {
        slideChange: () => {
          const navItems = this.querySelectorAll('.hero-style2-nav__item');

          navItems.forEach(item => item.classList.remove('active'));

          if (navItems[this.swiper.realIndex]) {
            navItems[this.swiper.realIndex].classList.add('active');
          }
        }
      }
    });
    // Style 2 navigation
const navItems = this.querySelectorAll('.hero-style2-nav__item');

if (navItems.length) {

  // Hover = slide change
  navItems.forEach((item) => {

    item.addEventListener('mouseenter', () => {
      const index = Number(item.dataset.slide);
      this.swiper.slideTo(index);
    });

  });

  // Active state update
  this.swiper.on('slideChange', () => {

    navItems.forEach((item) => {
      item.classList.remove('active');
    });

    const activeItem = this.querySelector(
      `.hero-style2-nav__item[data-slide="${this.swiper.realIndex}"]`
    );

    if (activeItem) {
      activeItem.classList.add('active');
    }

  });

  

} 
  }

  disconnectedCallback() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }
}

if (!customElements.get("hero-slideshow")) {
  customElements.define("hero-slideshow", HeroSlideshow);
}


class TimeLineSlide extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
  }

  connectedCallback() {
    this.initSlider();
  }

  initSlider() {
    const swiperEl = this.querySelector('.timeline__media');
    if (!swiperEl) return;

    // prevent double init
    if (swiperEl.classList.contains('swiper-initialized')) return;

    this.swiper = new Swiper(swiperEl, {
      slidesPerView: 1,
      spaceBetween: 10,
      speed: 600,
      observer: true,
      observeParents: true,

      navigation: {
        nextEl: this.querySelector('.swiper-button-next'),
        prevEl: this.querySelector('.swiper-button-prev'),
      },

      on: {
        init: function () {
          requestAnimationFrame(() => {
            this.update();
          });
        }
      }
    });
  }

  disconnectedCallback() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }
}

if (!customElements.get('time-line-slide')) {
  customElements.define('time-line-slide', TimeLineSlide);
} 


// EXPLORE MORE
class ExploreMore extends HTMLElement {
  constructor() {
    super();
    this.link = this.querySelector('.button');    
    this.next = this.closest('.shopify-section').nextElementSibling;   
}

  connectedCallback() {
    this.link.addEventListener('click', this.handleClick.bind(this));
  }

  handleClick(e) {
    e.preventDefault();

    let headerHeight = getComputedStyle(document.documentElement).getPropertyValue('--header-height').trim();
    headerHeight = headerHeight.split('px')
    const top = this.next.getBoundingClientRect().top + window.scrollY - Number(headerHeight[0]);
    if (window.lenis) {
    window.lenis.scrollTo(top, {
      duration: 1.5,
      easing: 'easeInOutCubic',
    });
  }
  }
}

customElements.define('explore-more', ExploreMore);


// COUNTDOWN TIMER
class CountdownTimer extends HTMLElement {
  constructor() {
    super();
    this.interval = null;
  }

  connectedCallback() {
    const wrapper = this.querySelector('.countdown_count_wrapper');
    if (!wrapper) return;

    const dateString = wrapper.dataset.date;
    if (!dateString) {
      this.invalidate();
      return;
    }

    const parsedDate = new Date(dateString.replace(" ", "T"));

    if (isNaN(parsedDate.getTime())) {
      this.invalidate();
      return;
    }

    this.targetDate = parsedDate;

    this.daysEl = this.querySelector("[data-days]");
    this.hoursEl = this.querySelector("[data-hours]");
    this.minutesEl = this.querySelector("[data-minutes]");
    this.secondsEl = this.querySelector("[data-seconds]");

    this.update();
    this.interval = setInterval(() => this.update(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.interval);
  }

  invalidate() {
    this.render(0, 0, 0, 0);
    clearInterval(this.interval);
    this.classList.add("is-invalid");
  }

  update() {
    const now = new Date();
    const diff = this.targetDate - now;

    if(diff <= 0) {
      this.render(0, 0, 0, 0);
      clearInterval(this.interval)
      this.classList.add('is_complete');
      return;
    }

    const seconds = Math.floor(diff / 1000);
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    this.render(days, hours, minutes, secs);
  }

  render(days, hours, minutes, seconds) {
    if (this.daysEl) this.daysEl.textContent = String(days).padStart(2, "0");
    if (this.hoursEl) this.hoursEl.textContent = String(hours).padStart(2, "0");
    if (this.minutesEl) this.minutesEl.textContent = String(minutes).padStart(2, "0");
    if (this.secondsEl) this.secondsEl.textContent = String(seconds).padStart(2, "0");
  }
}

customElements.define('countdown-timer', CountdownTimer);


/* ===============================
  TYPEWRITER PLACEHOLDER EFFECT
=============================== */
  
{
  const input = document.querySelector('#Search');

  if (input) {
    const searchPlaceholderLang =
      (document.documentElement.lang || 'en').toLowerCase();

    const words = searchPlaceholderLang.startsWith('fr')
      ? [
          'Rechercher par produit...',
          'Rechercher par collection...',
          'Rechercher par article de blog...',
          'Rechercher par article...',
          'Rechercher par page...'
        ]
      : searchPlaceholderLang.startsWith('es')
      ? [
          'Buscar por producto...',
          'Buscar por colección...',
          'Buscar por entrada del blog...',
          'Buscar por artículo...',
          'Buscar por página...'
        ]
      : searchPlaceholderLang.startsWith('zh')
      ? [
          '按产品搜索...',
          '按系列搜索...',
          '按博客文章搜索...',
          '按文章搜索...',
          '按页面搜索...'
        ]
      : searchPlaceholderLang.startsWith('ja')
      ? [
          '商品から検索...',
          'コレクションから検索...',
          'ブログ記事から検索...',
          '記事から検索...',
          'ページから検索...'
        ]
      : [
          'Search by product...',
          'Search by collection...',
          'Search by blog post...',
          'Search by article...',
          'Search by page...'
        ];

    let wordIndex = 0;
    let charIndex = 0;
    let isDeleting = false;

    function typeEffect() {
      const currentWord = words[wordIndex];

      if (!isDeleting) {
        input.setAttribute(
          'placeholder',
          currentWord.substring(0, charIndex + 1)
        );
        charIndex++;

        if (charIndex === currentWord.length) {
          setTimeout(() => {
            isDeleting = true;
          }, 1000);
        }
      } else {
        input.setAttribute(
          'placeholder',
          currentWord.substring(0, charIndex - 1)
        );
        charIndex--;

        if (charIndex === 0) {
          isDeleting = false;
          wordIndex = (wordIndex + 1) % words.length;
        }
      }

      setTimeout(typeEffect, isDeleting ? 50 : 100);
    }

    typeEffect();
  }
}
  


/* ===============================
  SEARCH CLEAR BUTTON
=============================== */
  const clearBtn = document.querySelector(".clear-btn");

  if (input && clearBtn) {

    function toggleClearBtn() {
      clearBtn.style.display = input.value.trim() ? "block" : "none";
    }

    toggleClearBtn();
    input.addEventListener("input", toggleClearBtn);

    clearBtn.addEventListener("click", function () {
      window.location.href = window.themeRoutes?.searchUrl || "/search";
    });
  }

  document.querySelectorAll('video').forEach(video => {
  const img = video.querySelector('img');
  if (!img) return;

  if (!img.hasAttribute('alt') || img.getAttribute('alt').trim() === '') {
    const poster = video.getAttribute('poster');
    img.setAttribute('alt', poster ? 'Video preview image' : 'Video preview');
  }
});



class ProductChart extends HTMLElement {

  connectedCallback() {

    if (this._initialized) return;
    this._initialized = true;

    this.trigger = this.querySelector(".size_chart_wrapper");
    this.popup = document.querySelector(".produc-size-chart");

    if (!this.trigger || !this.popup) return;

    this.inner = this.popup.querySelector(".produc-size-chart-in");
    this.closeBtn = this.popup.querySelector(".size_chart_popup_close");

    this.lastFocusedElement = null;

    this.handleKeydown = this.handleKeydown.bind(this);
    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.stopPropagation = this.stopPropagation.bind(this);

    this.trigger.addEventListener("click", this.open);
    this.popup.addEventListener("click", this.close);
    this.inner?.addEventListener("click", this.stopPropagation);
    this.closeBtn?.addEventListener("click", this.close);

    document.addEventListener("keydown", this.handleKeydown);
  }

  open(e) {
    e.preventDefault();

    this.lastFocusedElement = document.activeElement;

    this.popup.classList.add("active");
    this.popup.setAttribute("aria-hidden", "false");

    document.body.setAttribute("data-scroll-locked", "true");

    window.whenLenisReady(lenis => {
      lenis.stop();
    });

    // Focus first focusable element
    this.focusFirstElement();
  }

  close() {
    this.popup.classList.remove("active");
    this.popup.setAttribute("aria-hidden", "true");

    document.body.removeAttribute("data-scroll-locked");

    window.whenLenisReady(lenis => {
      lenis.start();
    });

    // Restore focus back to trigger
    this.lastFocusedElement?.focus();
  }

  getFocusableElements() {
    return this.popup.querySelectorAll(
      'a, button, input, textarea, select, [tabindex]:not([tabindex="-1"])'
    );
  }

  focusFirstElement() {
    const focusable = this.getFocusableElements();
    if (focusable.length) {
      focusable[0].focus();
    }
  }

  trapFocus(e) {
    const focusable = this.getFocusableElements();
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.key === "Tab") {
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        // Tab
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
  }

  handleKeydown(e) {
    if (!this.popup.classList.contains("active")) return;

    if (e.key === "Escape") {
      e.preventDefault();
      this.close();
      return;
    }

    this.trapFocus(e);
  }


  stopPropagation(e) {
    e.stopPropagation();
  }

  disconnectedCallback() {
    this.trigger?.removeEventListener("click", this.open);
    this.popup?.removeEventListener("click", this.close);
    this.inner?.removeEventListener("click", this.stopPropagation);
    this.closeBtn?.removeEventListener("click", this.close);
    document.removeEventListener("keydown", this.handleKeydown);
  }
}

customElements.define("product-chart", ProductChart);


if (!customElements.get('recipient-form')) {
  customElements.define(
    'recipient-form',  
    class RecipientForm extends HTMLElement {
      constructor() {
        super();

        this.recipientFieldsLiveRegion = this.querySelector(`#Recipient-fields-live-region-${this.dataset.sectionId}`);
        this.checkboxInput = this.querySelector(`#Recipient-checkbox-${this.dataset.sectionId}`);
        this.hiddenControlField = this.querySelector(`#Recipient-control-${this.dataset.sectionId}`);
        this.wrapper = this.querySelector('.recipient_fields_wrapper');
        this.inner = this.wrapper?.querySelector('.recipient_fields_inner');

        if (this.checkboxInput) this.checkboxInput.disabled = false;
        if (this.hiddenControlField) this.hiddenControlField.disabled = true;

        this.emailInput = this.querySelector(`#Recipient-email-${this.dataset.sectionId}`);
        this.nameInput = this.querySelector(`#Recipient-name-${this.dataset.sectionId}`);
        this.messageInput = this.querySelector(`#Recipient-message-${this.dataset.sectionId}`);
        this.sendonInput = this.querySelector(`#Recipient-send-on-${this.dataset.sectionId}`);
        this.offsetProperty = this.querySelector(`#Recipient-timezone-offset-${this.dataset.sectionId}`);

        if (this.offsetProperty) {
          this.offsetProperty.value = new Date().getTimezoneOffset().toString();
        }

        this.errorMessageWrapper = this.querySelector('.recipient_form_error_msg_wrapper');
        this.errorMessageList = this.errorMessageWrapper?.querySelector('ul');
        this.errorMessage = this.errorMessageWrapper?.querySelector('.error-message');
        this.defaultErrorHeader = this.errorMessage?.innerText;

        this.currentProductVariantId = this.dataset.productVariantId;

        this.addEventListener('change', this.onChange.bind(this));
        this.onChange();
      }

      cartUpdateUnsubscriber = undefined;
      variantChangeUnsubscriber = undefined;
      cartErrorUnsubscriber = undefined;

      connectedCallback() {
        if (!this.wrapper || !this.inner) return;

        const height = this.inner.clientHeight;
        this.wrapper.style.setProperty('--height', `${height}px`);
      }

      onChange() {
          if (!this.checkboxInput) return;

          const accessibility = window.accessibilityStrings || {};

          if (this.checkboxInput.checked) {
            this.enableInputFields();

            if (this.recipientFieldsLiveRegion) {
              this.recipientFieldsLiveRegion.innerText =
                accessibility.recipientFormExpanded || 'Recipient form expanded';
            }

            this.wrapper?.classList.add('visible');
          } else {
            this.clearInputFields();
            this.disableInputFields();
            this.clearErrorMessage();
 
            this.wrapper?.classList.remove('visible');
  
            if (this.recipientFieldsLiveRegion) {
              this.recipientFieldsLiveRegion.innerText =
                accessibility.recipientFormCollapsed || 'Recipient form collapsed';
            } 
          }
        }
      inputFields() {
        return [this.emailInput, this.nameInput, this.messageInput, this.sendonInput];
      }

      disableableFields() {
        return [...this.inputFields(), this.offsetProperty];
      }

      clearInputFields() {
        this.inputFields().forEach((field) => (field.value = ''));
      }

      enableInputFields() {
        this.disableableFields().forEach((field) => (field.disabled = false));
      }

      disableInputFields() {
        this.disableableFields().forEach((field) => (field.disabled = true));
      }

      displayErrorMessage(title, body) {
        this.clearErrorMessage();
            document.querySelector('.error_message_wrapper').style.display = 'none';            
        
        this.errorMessageWrapper.hidden = false;
        if (typeof body === 'object') {
          this.errorMessage.innerText = this.defaultErrorHeader;
          return Object.entries(body).forEach(([key, value]) => {
            const errorMessageId = `RecipientForm-${key}-error-${this.dataset.sectionId}`;
            const fieldSelector = `#Recipient-${key}-${this.dataset.sectionId}`;
            const message = `${value.join(', ')}`;
            const errorMessageElement = this.querySelector(`#${errorMessageId}`);  
            const errorTextElement = errorMessageElement?.querySelector('.error-message');
            if (!errorTextElement) return;

            if (this.errorMessageList) {
              this.errorMessageList.appendChild(this.createErrorListItem(fieldSelector, message));
            }

            errorTextElement.innerText = `${message}.`;
            errorMessageElement.classList.remove('hidden');

            const inputElement = this[`${key}Input`];
            if (!inputElement) return;

            inputElement.setAttribute('aria-invalid', true);
            inputElement.setAttribute('aria-describedby', errorMessageId);
          });
        }

        this.errorMessage.innerText = body;
      }

      createErrorListItem(target, message) {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.setAttribute('href', target);
        a.innerText = message;
        li.appendChild(a);
        li.className = 'error-message';
        return li;
      }

      clearErrorMessage() {
        this.errorMessageWrapper.hidden = true;

        if (this.errorMessageList) this.errorMessageList.innerHTML = '';

        this.querySelectorAll('.recipient-fields .form__message').forEach((field) => {
          field.classList.add('hidden');
          const textField = field.querySelector('.error-message');
          if (textField) textField.innerText = '';
        });

        [this.emailInput, this.messageInput, this.nameInput, this.sendonInput].forEach((inputElement) => {
          inputElement.setAttribute('aria-invalid', false);
          inputElement.removeAttribute('aria-describedby');
        });
      }

      resetRecipientForm() {
        if (this.checkboxInput.checked) {
          this.checkboxInput.checked = false;
          this.clearInputFields();
          this.clearErrorMessage();
        }
      }
    }
  ); 
}
   
  window.addEventListener('load', () => {
    const hero = document.querySelector('.hero_banner_wrapper');
    if (!hero) return;

    hero.classList.add('loaded');
  });

 